/**
 * @file endpoints.ts
 * @brief Single source of truth for all backend endpoint paths.
 *
 * @details
 * Every feature module imports paths from here instead of hard-coding
 * URLs. This makes it trivial to:
 * - Bulk-rename a path (e.g. when migrating to `/api/v1/*`).
 * - Audit which endpoints the panel consumes.
 * - Stay in sync with backend route declarations in
 *   `BACKEND/src/app.js`.
 *
 * @references
 * - BACKEND/src/app.js (route mounts)
 * - BACKEND/src/features/auth/routes.js
 * - BACKEND/src/features/admin/routes.js
 * - BACKEND/src/features/company/routes.js
 *
 * @author DES
 * @date 26-05-2026
 */

// ----------------------------------------------------------------------
// Auth (BACKEND/src/features/auth/routes.js, mounted at /api/auth)
// ----------------------------------------------------------------------
export const authEndpoints = {
  login: "/api/auth/login",
  google: "/api/auth/google",
  apple: "/api/auth/apple",
  /** Sign in with Microsoft (06-10-2026). */
  microsoft: "/api/auth/microsoft",
  /** Signed-in devices, at most 3 (one account = one person; proposed contract, 18-10-2026). */
  devices: "/api/auth/devices",
  deviceById: (id: string) => `/api/auth/devices/${encodeURIComponent(id)}`,
  /** One-tap link from a billing email: single use, 15 min (BACKEND A6, 18-10-2026). */
  magicVerify: "/api/auth/magic/verify",
  /** Free plan (B4, 22-10-2026): send the confirm-email link again (max 1 per 10 min). */
  verifyEmail: "/api/auth/verify-email",
  logout: "/api/auth/logout",
  me: "/api/auth/me",
  /** "Use my data to improve models" (ADR-048, 20-10-2026). GET / PUT { optIn, version }. */
  modelTraining: "/api/auth/model-training",
  /** "How VERBLIZR uses AI" (B7, 22-10-2026). GET state / POST { version }. */
  aiConsent: "/api/auth/ai-consent",
  /** Company member: GET the open request / POST { reason? } to ask for deletion (B7.2). */
  deletionRequest: "/api/auth/account/deletion-request",
  /** Effective feature toggles for the signed-in user (15-07-2026). */
  entitlements: "/api/auth/entitlements",
  forgotInit: "/api/auth/forgot-password/init",
  forgotVerify: "/api/auth/forgot-password/verify",
  forgotReset: "/api/auth/forgot-password/reset",
  /* Profile self-service — same routes the mobile app uses for the
     signed-in user. Web panel reuses them as-is. */
  profile: "/api/auth/profile",
  uploadProfileImage: "/api/auth/upload-profile-image",
  changePassword: "/api/auth/change-password",
  /** DELETE, body { currentPassword?, confirm }: the signed-in user closes their own account. */
  deleteAccount: "/api/auth/account",
  /** GET, zip: "Download my data" (GDPR Art 15/20). Individual accounts only. */
  exportData: "/api/auth/export",
  /** POST, no body: the same archive (plus PDFs) emailed as a 7-day link (ADR-046). */
  exportDataEmail: "/api/auth/export/email",
  /** GET (public): current Terms + Privacy version and links (06-10-2026). */
  termsInfo: "/api/auth/terms",
  /** POST { version }: the signed-in user accepts that version. */
  acceptTerms: "/api/auth/accept-terms",
  /* Sign-up (01-10-2026): the phone's public routes, as-is. */
  register: "/api/auth/register",
  registerCompany: "/api/auth/register-company",
  checkEmail: "/api/auth/check-email",
  onboarding: "/api/auth/onboarding",
  /* Voice enrollment (04-10-2026): the phone's routes, as-is. */
  voiceEnrollmentStatus: "/api/auth/voice-enrollment/status",
  voiceEnrollmentRequestUpload: "/api/auth/voice-enrollment/request-upload",
  voiceEnrollmentComplete: "/api/auth/voice-enrollment/complete",
  voiceEnrollment: "/api/auth/voice-enrollment",
  /** POST: "Remove my voice and withdraw consent"; deletes the voiceprint now (ADR-046). */
  voiceConsentWithdraw: "/api/auth/voice-consent/withdraw",
  /** GET → { current: { given, version, at }, history } (ADR-046). */
  voiceConsent: "/api/auth/voice-consent",
  /* Two-step verification (B31, ADR-059, 25-10-2026). Contract:
     docs/product/two-step-verification.md. */
  twoFactorVerify: "/api/auth/2fa/verify",
  twoFactorStatus: "/api/auth/2fa/status",
  twoFactorSetupStart: "/api/auth/2fa/setup/start",
  twoFactorSetupConfirm: "/api/auth/2fa/setup/confirm",
  twoFactorDisable: "/api/auth/2fa/disable",
  twoFactorBackupCodes: "/api/auth/2fa/backup-codes",
  twoFactorTrustedDevices: "/api/auth/2fa/trusted-devices",
} as const;

// ----------------------------------------------------------------------
// Admin (BACKEND/src/features/admin/routes.js, mounted at /api/admin)
// All routes require `requireSuperAdmin`.
// ----------------------------------------------------------------------
export const adminEndpoints = {
  // Users
  users: "/api/admin/users",
  userById: (id: string) => `/api/admin/users/${id}`,
  userLoginHistory: (id: string) => `/api/admin/users/${id}/login-history`,
  userSuspend: (id: string) => `/api/admin/users/${id}/suspend`,
  userReactivate: (id: string) => `/api/admin/users/${id}/reactivate`,
  userChangePassword: (id: string) => `/api/admin/users/${id}/change-password`,
  userResendWelcome: (id: string) => `/api/admin/users/${id}/resend-welcome`,
  /** POST: emails one person's data to the ACCOUNT address as a 7-day link,
   *  for a GDPR request sent to Verblizr. Staff never get the zip (B2/W4,
   *  22-10-2026; the old GET answers 405 EXPORT_EMAILED_ONLY). */
  userExport: (id: string) => `/api/admin/users/${id}/export`,
  /** POST: turn off a user's two-step verification (B31); the Owner only for a Super Admin. */
  userTwoFactorReset: (id: string) => `/api/admin/users/${id}/2fa/reset`,
  /** GET staff 2FA state; PUT { staffRequired } (Owner only, B31). */
  staffTwoFactor: "/api/admin/security/two-factor",

  /** Activity log, every company (05-10-2026). ?companyId=&before=&limit= */
  activity: "/api/admin/activity",

  // Companies
  companies: "/api/admin/companies",
  companyById: (id: string) => `/api/admin/companies/${id}`,
  companySuspend: (id: string) => `/api/admin/companies/${id}/suspend`,
  companyReactivate: (id: string) => `/api/admin/companies/${id}/reactivate`,
  companyEnterprise: (id: string) => `/api/admin/companies/${id}/enterprise`,
  companyEnterpriseStatus: (id: string) =>
    `/api/admin/companies/${id}/enterprise/status`,
  companyEnterpriseSeats: (id: string) =>
    `/api/admin/companies/${id}/enterprise/seats`,
  companyEnterpriseSeatByUser: (id: string, userId: string) =>
    `/api/admin/companies/${id}/enterprise/seats/${userId}`,
  /** Plans → Enterprise contact-sales leads */
  enterpriseInquiries: "/api/admin/enterprise-inquiries",
  enterpriseInquiryById: (id: string) =>
    `/api/admin/enterprise-inquiries/${id}`,

  // Sessions (cross-platform list/detail/delete) — DES Added: (26-05-2026)
  sessions: "/api/admin/sessions",
  sessionById: (id: string) => `/api/admin/sessions/${id}`,
  // No staff transcript route: 403 STAFF_NO_CONTENT (B2/W4, 22-10-2026).
  /* Per-asset destructive variants — mirror the mobile long-press
     menu sub-options. The plain `sessionById` DELETE wipes everything
     (the "Delete All Data" choice). */
  sessionAudio: (id: string) => `/api/admin/sessions/${id}/audio`,
  sessionArtifacts: (id: string) => `/api/admin/sessions/${id}/artifacts`,

  // Boardroom / MeetingIntel — platform-wide (BACKEND/src/features/boardroom/
  // admin.routes.js, mounted at /api/admin/boardroom, requireSuperAdmin).
  // Super admin reads + manages ANY meeting; list adds owner + company columns.
  // List accepts ?page=&pageSize=&search=&from=&to=&owner_id=&company_id=.
  // Consumed by: features/admin-meetings/* (Phase B, 08-07-2026).
  boardroomSessions: "/api/admin/boardroom/sessions",
  boardroomSessionById: (id: string) => `/api/admin/boardroom/sessions/${id}`,
  boardroomReprocessDiarization: (id: string) =>
    `/api/admin/boardroom/sessions/${id}/reprocess-diarization`,
  boardroomRegenerateIntelligence: (id: string) =>
    `/api/admin/boardroom/sessions/${id}/regenerate-intelligence`,
  // Vendor cost (COGS) next to the bill; super-admin router only.
  boardroomCogs: (id: string) => `/api/admin/boardroom/sessions/${id}/cogs`,
  // No resolve / translate / export for VERBLIZR staff: those answer 403
  // STAFF_NO_CONTENT (B2/W4, DES 22-10-2026).
  boardroomDelete: (id: string) => `/api/admin/boardroom/sessions/${id}`,

  // Analytics
  analyticsSessions: "/api/admin/analytics/sessions",
  analyticsSessionsTrend: "/api/admin/analytics/sessions/trend",
  analyticsSessionsPerUser: "/api/admin/analytics/sessions/per-user",
  analyticsSessionsPerCompany: "/api/admin/analytics/sessions/per-company",
  analyticsRevenue: "/api/admin/analytics/revenue",
  analyticsRevenueTrend: "/api/admin/analytics/revenue/trend",
  analyticsLanguages: "/api/admin/analytics/languages",
  /** B25 (W22, 25-10-2026): overview | costs | margin | saas | subscribers-by-price | languages. */
  analyticsBusiness: "/api/admin/analytics/business",
  analyticsTokens: "/api/admin/analytics/tokens",
  analyticsTokensPerUser: "/api/admin/analytics/tokens/per-user",
  growthUsers: "/api/admin/analytics/growth/users",
  growthCompanies: "/api/admin/analytics/growth/companies",
  /** Phase A Reports — docs/product/reporting.md (27-07-2026) */
  reportsRevenueByProduct: "/api/admin/reports/revenue-by-product",
  reportsCompanyHealth: "/api/admin/reports/company-health",
  reportsBillingAnomalies: "/api/admin/reports/billing-anomalies",
  reportsEnterprisePipeline: "/api/admin/reports/enterprise-pipeline",
  reportsQualityOps: "/api/admin/reports/quality-ops",
  /** Phase C — server CSV/PDF export (`{reportKey}.csv|.pdf`). */
  reportsExport: (reportKey: string, format: "csv" | "pdf") =>
    `/api/admin/reports/export/${encodeURIComponent(reportKey)}.${format}`,

  // Settings & pricing
  settings: "/api/admin/settings",
  pricingConfig: "/api/admin/pricing-config",
  // Plans block-list (BACKEND B1, 22-10-2026): GET + PUT `{ blocked, reason }`.
  billingCountries: "/api/admin/billing-countries",
  // Free minutes once per account + device, disposable email list (BACKEND B43, 31-10-2026).
  freeGrants: "/api/admin/free-grants",
  // The four launch plans as cards (BACKEND B3, 22-10-2026), read-only.
  plans: "/api/admin/plans",
  // Who a plan setting change reaches, before Save (BACKEND B20, 24-10-2026).
  planImpact: "/api/admin/plans/impact",
  // Links & company info (BACKEND B23, 24-10-2026): GET + PUT `{ settings }`.
  linkSettings: "/api/admin/link-settings",
  // Zapier / Make platform + per-company switches (BACKEND B42, 30-10-2026).
  integrationsPlatform: "/api/admin/integrations/platform",
  integrationsCompanies: "/api/admin/integrations/companies",
  integrationsCompany: (id: string) => `/api/admin/integrations/companies/${encodeURIComponent(id)}`,
  // AI provider→model switchboard catalog (providers, models, configured
  // flags — never credential values). (11-07-2026)
  aiCatalog: "/api/admin/ai/catalog",

  // System / cost alerts
  costStatus: "/api/admin/cost-status",
  checkCostAlert: "/api/admin/check-cost-alert",
  // One real test email to COST_ALERT_EMAIL, superadmin only (06-10-2026).
  testCostAlert: "/api/admin/cost-alert/test",
  /* Wave 4a (26-05-2026) — Billing & Pricing tools.
     Re-runs cost computation across all sessions in a date range using
     the current `pricing_config` rows; useful after editing OpenAI
     service rates. */
  recomputeSessionCosts: "/api/admin/analytics/recompute-session-costs",

  // Languages
  languages: "/api/admin/languages",
  languageStatus: "/api/admin/languages/status",
} as const;

// ----------------------------------------------------------------------
// Feedback (BACKEND/src/features/feedback/routes.js, mounted at /api/feedback)
// GET routes require `requireSuperAdmin`.
// ----------------------------------------------------------------------
export const feedbackEndpoints = {
  sessions: "/api/feedback",
  app: "/api/feedback/app",
  /** User submit / read own session rating (Phase C web parity). */
  session: "/api/feedback/session",
  sessionById: (id: string) => `/api/feedback/session/${id}`,
} as const;

// ----------------------------------------------------------------------
// Banners (BACKEND/src/features/banners/routes.js, mounted at /api/banners)
// Admin routes require `requireSuperAdmin`.
// ----------------------------------------------------------------------
export const bannerEndpoints = {
  list: "/api/banners",
  active: "/api/banners/active",
  upload: "/api/banners/upload",
  create: "/api/banners",
  byId: (id: string) => `/api/banners/${id}`,
  reorder: "/api/banners/reorder",
} as const;

// ----------------------------------------------------------------------
// Company admin (BACKEND/src/features/company/routes.js, /api/company)
// Requires `requireAuth` + role in {admin, superadmin}.
// ----------------------------------------------------------------------
export const companyEndpoints = {
  /** PUBLIC (no auth): Register Business checks the name as it is typed. */
  checkName: "/api/company/check-name",
  /** Company admin (B7.2): GET what deleting the company does + members' requests;
   *  DELETE { confirm: company name, currentPassword } deletes the whole company. */
  deletion: "/api/company/deletion",
  company: "/api/company",
  /** Own company's activity log (05-10-2026). ?before=&limit= */
  activity: "/api/company/activity",
  users: "/api/company/users",
  userById: (id: string) => `/api/company/users/${id}`,
  userLoginHistory: (id: string) => `/api/company/users/${id}/login-history`,
  userStatus: (id: string) => `/api/company/users/${id}/status`,
  userPassword: (id: string) => `/api/company/users/${id}/password`,
  /** GET, zip: one employee's data, for their GDPR request (05-10-2026). */
  userExport: (id: string) => `/api/company/users/${id}/export`,
  userPaymentMethod: (id: string) =>
    `/api/company/users/${id}/payment-method`,
  userResendWelcome: (id: string) => `/api/company/users/${id}/resend-welcome`,
  profile: "/api/company/profile",
  /** "Allow members to share data for model improvement" (ADR-048, 20-10-2026). GET / PUT { allowMembers }. */
  modelTraining: "/api/company/model-training",
  /** "Require two-step verification for all members" (B31): GET / PUT { require2fa }. */
  security: "/api/company/security",
  /** Company data controls (ADR-051, 21-10-2026): GET / PUT { shareInsideOnly?, adminsToo?, allowExport? }; `{}` marks the notice seen. */
  dataControls: "/api/company/data-controls",
  dataControlsExternalShares: "/api/company/data-controls/external-shares",
  dataControlsRemoveAllExternal: "/api/company/data-controls/external-shares/remove-all",
  // Feature on/off toggles for the company's regular employees (admin-managed).
  features: "/api/company/features",
  // Enterprise shared pool + seat assign (26-07-2026).
  enterprise: "/api/company/enterprise",
  enterpriseSeats: "/api/company/enterprise/seats",
  enterpriseSeatByUser: (userId: string) =>
    `/api/company/enterprise/seats/${userId}`,
  // Analytics — company-admin scope. Every endpoint accepts `?scope=`
  // (`personal` | `company`); the backend forces the actual user/company id
  // from the auth token, so the client only ever picks which lens to view.
  analyticsSessions: "/api/company/analytics/sessions",
  analyticsSessionsTrend: "/api/company/analytics/sessions/trend",
  analyticsSessionsPerUser: "/api/company/analytics/sessions/per-user",
  analyticsRevenue: "/api/company/analytics/revenue",
  analyticsRevenueTrend: "/api/company/analytics/revenue/trend",
  analyticsLanguages: "/api/company/analytics/languages",
  /** Phase B Reports — docs/product/reporting.md (27-07-2026) */
  reportsUsageByEmployee: "/api/company/reports/usage-by-employee",
  reportsSpendByProduct: "/api/company/reports/spend-by-product",
  reportsMeetingIntel: "/api/company/reports/meeting-intel",
  reportsProductMix: "/api/company/reports/product-mix",
  reportsScheduleOps: "/api/company/reports/schedule-ops",
  reportsEnrollment: "/api/company/reports/enrollment",
  reportsAdoption: "/api/company/reports/adoption",
  /** Phase C — server CSV/PDF export (`{reportKey}.csv|.pdf`). */
  reportsExport: (reportKey: string, format: "csv" | "pdf") =>
    `/api/company/reports/export/${encodeURIComponent(reportKey)}.${format}`,
} as const;

// ----------------------------------------------------------------------
// Schedule (BACKEND/src/features/scheduler/routes.js, /api/schedule)
// Company admins book upcoming interpretation appointments.
// ----------------------------------------------------------------------
export const scheduleEndpoints = {
  list: "/api/schedule",
  byId: (id: string) => `/api/schedule/${id}`,
  users: "/api/schedule/users",
  languages: "/api/s2s/languages",
} as const;

// ----------------------------------------------------------------------
// Live interpretation (BACKEND/src/features/s2s/routes.mjs, /api/s2s)
// The same endpoints the phone's interpreter screen calls. 01-10-2026.
// ----------------------------------------------------------------------
export const s2sEndpoints = {
  start: "/api/s2s/start",
  end: "/api/s2s/end",
  cancel: "/api/s2s/cancel",
  languages: "/api/s2s/languages",
  /** The other person's "keep my voice?" sheet copy + spoken clip (04-10-2026). */
  voiceConsentPrompt: "/api/s2s/voice-consent-prompt",
} as const;

// ----------------------------------------------------------------------
// Meetings — participant lookup for external clients (boardroom setup)
// BACKEND/src/features/meetings/routes.js
// ----------------------------------------------------------------------
export const meetingsEndpoints = {
  list: "/api/meetings",
  byId: (id: string) => `/api/meetings/${id}`,
  participantLookup: "/api/meetings/participant-lookup",
} as const;

// ----------------------------------------------------------------------
// Sessions (BACKEND/src/features/sessions/routes.js, /api/sessions)
// ----------------------------------------------------------------------
export const sessionEndpoints = {
  list: "/api/sessions",
  byId: (id: string) => `/api/sessions/${id}`,
  transcript: (id: string) => `/api/sessions/${id}/transcript`,
  /** GET, zip: the session's original + translated audio (the phone's Audios). */
  audioZip: (id: string) => `/api/sessions/${id}/session.zip`,
  deleteAll: (id: string) => `/api/sessions/${id}/all`,
  deleteAudio: (id: string) => `/api/sessions/${id}/audio`,
  deleteArtifacts: (id: string) => `/api/sessions/${id}/artifacts`,
} as const;

// ----------------------------------------------------------------------
// Invoices (BACKEND/src/features/invoices/routes.js, /api/invoices)
// ----------------------------------------------------------------------
export const invoiceEndpoints = {
  list: "/api/invoices",
  byId: (id: string) => `/api/invoices/${id}`,
  export: "/api/invoices/export",
} as const;

// ----------------------------------------------------------------------
// Billing (BACKEND/src/features/billing/routes.js, /api/billing)
// Company admins pass `?company_id=` to manage corporate Stripe customer.
// ----------------------------------------------------------------------
export const billingEndpoints = {
  customer: "/api/billing/customer",
  setupIntent: "/api/billing/setup-intent",
  paymentMethodDefault: (id: string) =>
    `/api/billing/payment-methods/${id}/default`,
  paymentMethodDelete: (id: string) =>
    `/api/billing/payment-methods/${id}/delete`,
  paymentMethodById: (id: string) => `/api/billing/payment-methods/${id}`,
  companyStatus: "/api/billing/company/status",
  /** Card gate + hold before boardroom/notes capture (Phase B web parity). */
  preauthorize: "/api/billing/preauthorize",
  /** Release your own unused hold, e.g. a discarded recording (02-10-2026). */
  preauthorizeRelease: "/api/billing/preauthorize/release",
  pricing: "/api/billing/pricing",
  /** Live included-minutes signal while recording (B5, 22-10-2026). Display only. */
  usageLive: "/api/billing/usage/live",
  /** Plans (ADR-019): GET status + catalogue, POST to subscribe (17-10-2026). */
  subscriptions: "/api/billing/subscriptions",
  subscriptionsCatalog: "/api/billing/subscriptions/catalog",
  /** Re-reads Stripe after the first payment (3DS); the client is not trusted. */
  subscriptionsSync: "/api/billing/subscriptions/sync",
  subscriptionsCancel: "/api/billing/subscriptions/cancel",
  /** Switch plan / interval with proration; undo a cancel (BACKEND A2, 18-10-2026). */
  subscriptionsChange: "/api/billing/subscriptions/change",
  subscriptionsResume: "/api/billing/subscriptions/resume",
  /** Newest unseen plan change for this user (plans-visible Part 1, 21-10-2026). */
  planNotice: "/api/billing/plan-notice",
  planNoticeSeen: "/api/billing/plan-notice/seen",
  /** Exact proration before a change; changes nothing (BACKEND 998f048, 18-10-2026). */
  subscriptionsChangePreview: "/api/billing/subscriptions/change/preview",
} as const;

// ----------------------------------------------------------------------
// Personal Notes (BACKEND/src/features/notes/routes.js, mounted at
// /api/notes). USER-scoped, not company-scoped: every route filters on
// `owner_id = req.user.id`, so a company admin reads their own notes here and
// never a member's — unlike boardroom, which widens for admins.
//
// The web panel writes only TYPED notes: `POST /notes` with a `text` body is
// what makes a note typed, and typed notes are unmetered and never touch ASR
// or insights, so no billing pre-authorisation path is needed here.
// Recording stays on mobile; the web reads and plays what it produced.
// Related: docs/product/voice-notes.md, ADR-023 (21-08-2026)
// ----------------------------------------------------------------------
export const notesEndpoints = {
  list: "/api/notes",
  /** POST with `{ title, text }` — the `text` body is what makes it typed. */
  create: "/api/notes",
  byId: (id: string) => `/api/notes/${id}`,
  /** PATCH `{ title?, text?, tags?, isPinned? }`. `text` is refused for a
   *  voice note server-side — only a typed note's body may be edited. */
  update: (id: string) => `/api/notes/${id}`,
  delete: (id: string) => `/api/notes/${id}`,
  /** Distinct tags across the caller's notes, most-used first. Registered
   *  before `/:noteId` server-side, or "tags" parses as a note id. */
  tags: "/api/notes/tags",
  /** Meetings this note is attached to — the note side of the ADR-023 join. */
  links: (id: string) => `/api/notes/${id}/links`,
  /** Re-derive a recording's summary and points (ADR-025). Kept at the old
   *  `regenerate-insights` path so existing clients keep working; what it
   *  regenerates changed, not where it lives. Answers 409 once the points have
   *  been edited, unless the body carries `force`. */
  regenerate: (id: string) => `/api/notes/${id}/regenerate-insights`,
  /** "My notes x this meeting" (29-09-2026; web 06-10-2026): GET lists the
   *  note's linked meetings with their report state; POST `{ force? }` on
   *  `meetingReport` builds one (a model call, 10-30s). Author only. */
  meetingReports: (id: string) => `/api/notes/${id}/meeting-reports`,
  meetingReport: (id: string, meetingId: string) =>
    `/api/notes/${id}/meeting-reports/${encodeURIComponent(meetingId)}`,
  // The action-item endpoint is GONE (ADR-025): a tick promised follow-through
  // this product does not provide — nothing reminded you, and nothing surfaced
  // unticked items anywhere.
} as const;

// ----------------------------------------------------------------------
// Boardroom / MeetingIntel (BACKEND/src/features/boardroom/routes.js,
// mounted at /api/boardroom). Company admins read + manage any meeting
// owned by a member of their company (owner's users.company_id scope —
// see boardroom/routes.js `ownerOrCompanyClause`, added 08-07-2026).
// List accepts ?page=&pageSize=&search=&from=&to=&employee_id=.
// Consumed by: features/company-meetings/* (Phase 2, 08-07-2026).
// ----------------------------------------------------------------------
export const boardroomEndpoints = {
  list: "/api/boardroom/sessions",
  byId: (id: string) => `/api/boardroom/sessions/${id}`,
  /** Ask across meetings (or one, with `meetingId`). POST — the question is a
   *  body, not a query string, so it never lands in a URL or an access log.
   *  (03-08-2026) */
  search: "/api/boardroom/sessions/search",
  // Read-only: the remaining allowance WITHOUT spending one.
  searchQuota: "/api/boardroom/sessions/search/quota",
  /** Signed GCS PUT for browser/mobile capture (28-07-2026 web parity). */
  requestUpload: "/api/boardroom/sessions/request-upload",
  create: "/api/boardroom/sessions",
  /** B49: the recorder's agenda items (own agenda notes, up to 5). 404 = switch off. */
  agendaItems: (noteIds: string[]) =>
    `/api/boardroom/agenda-items?noteIds=${noteIds.map(encodeURIComponent).join(",")}`,
  resolve: (id: string) => `/api/boardroom/sessions/${id}/resolve`,
  autoSynthesize: (id: string) =>
    `/api/boardroom/sessions/${id}/auto-synthesize`,
  reprocessDiarization: (id: string) =>
    `/api/boardroom/sessions/${id}/reprocess-diarization`,
  regenerateIntelligence: (id: string) =>
    `/api/boardroom/sessions/${id}/regenerate-intelligence`,
  exportMom: (id: string) => `/api/boardroom/sessions/${id}/export-mom`,
  exportTranscript: (id: string) =>
    `/api/boardroom/sessions/${id}/export-transcript`,
  /** B50: Export / Send — GET the catalogue; POST preview | text | pdf. */
  exportPick: (id: string) => `/api/boardroom/sessions/${id}/export`,
  exportPickPreview: (id: string) => `/api/boardroom/sessions/${id}/export/preview`,
  exportPickText: (id: string) => `/api/boardroom/sessions/${id}/export/text`,
  exportPickPdf: (id: string) => `/api/boardroom/sessions/${id}/export/pdf`,
  translate: (id: string) => `/api/boardroom/sessions/${id}/translate`,
  /** PATCH `{completed}` — one task, deliberately NOT the whole `todos` array:
   *  two people ticking at once would overwrite each other, and a shared viewer
   *  would be rewriting the owner's tasks to change their own. The owner /
   *  assignee rule is enforced server-side. (04-08-2026) */
  toggleTodo: (id: string, todoId: string) =>
    `/api/boardroom/sessions/${id}/todos/${encodeURIComponent(todoId)}`,
  /** Manual to-dos: add one, or remove one that was added by hand. (05-08-2026) */
  addTodo: (id: string) => `/api/boardroom/sessions/${id}/todos`,
  /** PUT — edits a MANUAL to-do's text, assignee and due date. */
  updateTodo: (id: string, todoId: string) =>
    `/api/boardroom/sessions/${id}/todos/${encodeURIComponent(todoId)}`,
  deleteTodo: (id: string, todoId: string) =>
    `/api/boardroom/sessions/${id}/todos/${encodeURIComponent(todoId)}`,
  /** Dismiss / restore an AI to-do — PUT { dismissed } (05-10-2026). */
  dismissTodo: (id: string, todoId: string) =>
    `/api/boardroom/sessions/${id}/todos/${encodeURIComponent(todoId)}/dismiss`,
  /** Who follows up an external to-do — PUT { userId | null } (05-10-2026). */
  todoFollowUp: (id: string, todoId: string) =>
    `/api/boardroom/sessions/${id}/todos/${encodeURIComponent(todoId)}/follow-up`,
  /** Who a manual to-do may be assigned to — company members, or just self. */
  assignablePeople: "/api/boardroom/assignable-people",
  /** "My to-dos" across every meeting I can see, grouped by due date. GET
   *  ?status=open|overdue|done &scope=mine|all &q= &tz= &view=home &limit=
   *  (12-10-2026). Ticking still goes through `toggleTodo`. */
  myTodos: "/api/boardroom/my-todos",
  /** To-do grouping (F1a, ADR-062): the learned list (GET ?meetingId=),
   *  rename (PATCH { name }), merge (POST { fromId, intoId }), move one
   *  to-do (PATCH { categoryId | categoryName | topic }), rename a topic in
   *  one meeting (PATCH { from, to }). */
  todoCategories: "/api/boardroom/todo-categories",
  todoCategory: (categoryId: string) => `/api/boardroom/todo-categories/${encodeURIComponent(categoryId)}`,
  mergeTodoCategories: "/api/boardroom/todo-categories/merge",
  todoGrouping: (id: string, todoId: string) =>
    `/api/boardroom/sessions/${id}/todos/${encodeURIComponent(todoId)}/grouping`,
  todoTopics: (id: string) => `/api/boardroom/sessions/${id}/todo-topics`,
  /** Personal notes attached to a meeting (ADR-023). Owner-only on READ too —
   *  a prep note is the owner's private text, so a 404 here is the expected
   *  answer for anyone else, not a failure. (21-08-2026) */
  prepNotes: (id: string) => `/api/boardroom/sessions/${id}/prep-notes`,
  // B48: "Add email" for a name-only guest on this meeting (owner only).
  guestEmail: (id: string) => `/api/boardroom/sessions/${id}/people/email`,
  delete: (id: string) => `/api/boardroom/sessions/${id}`,
  /** Owner-only. Meeting Notes -> full Meeting Intelligence (ADR-030,
   *  06-10-2026): re-runs the full pipeline on the same recording. */
  upgrade: (id: string) => `/api/boardroom/sessions/${id}/upgrade`,
  /** Owner-only. Correct a selection in the transcript (`scope` "one" at
   *  `offset`, or "all"); carried into the intel by context. (27-09-2026) */
  corrections: (id: string) => `/api/boardroom/sessions/${id}/corrections`,
  /** Owner-only. "Who said this?": move a selection to another speaker; the
   *  turn is split at whole sentences. (29-09-2026) */
  speakerMoves: (id: string) => `/api/boardroom/sessions/${id}/speaker-moves`,
  /** Change a whole speaker (02-10-2026) and its one-step Undo. */
  speakerSwap: (id: string) => `/api/boardroom/sessions/${id}/speaker-swap`,
  speakerSwapUndo: (id: string) => `/api/boardroom/sessions/${id}/speaker-swap/undo`,
} as const;

// ----------------------------------------------------------------------
// Address autocomplete (BACKEND/src/features/address/routes.js)
// Geoapify proxied server-side — GEOAPIFY_API_KEY never leaves the backend.
// Shared by mobile + WEBPANEL (27-07-2026).
// ----------------------------------------------------------------------
export const addressEndpoints = {
  autocomplete: "/api/address/autocomplete",
} as const;

// ----------------------------------------------------------------------
// Medical Assessment (BACKEND/src/features/medical-assessment/routes.js)
// ----------------------------------------------------------------------
export const medicalAssessmentEndpoints = {
  list: "/api/medical-assessment",
  inbox: "/api/medical-assessment/inbox",
  create: "/api/medical-assessment",
  acceptIntake: (id: string) => `/api/medical-assessment/${id}/intake/accept`,
  byId: (id: string) => `/api/medical-assessment/${id}`,
  statements: (id: string) => `/api/medical-assessment/${id}/statements`,
  statement: (assessmentId: string, statementId: string) =>
    `/api/medical-assessment/${assessmentId}/statements/${statementId}`,
  /** Accept a finding the recording does not support; DELETE the same path removes it. */
  verifyStatement: (assessmentId: string, statementId: string) =>
    `/api/medical-assessment/${assessmentId}/statements/${statementId}/verify`,
  demoSeedAleyada: "/api/medical-assessment/demo/seed-aleyada",
  caseHubLookup: "/api/medical-assessment/case-hub/lookup",
  clinicScheduleLookup: "/api/medical-assessment/clinic-schedule/lookup",
  doctorsLookup: "/api/medical-assessment/doctors/lookup",
  createFromAppointment: "/api/medical-assessment/from-appointment",
  caseHubPull: (id: string) => `/api/medical-assessment/${id}/case-hub/pull`,
  sessionLink: (id: string) => `/api/medical-assessment/${id}/session/link`,
  transcript: (id: string) => `/api/medical-assessment/${id}/transcript`,
  autoGenerate: (id: string) => `/api/medical-assessment/${id}/auto-generate`,
  exportGripXml: (id: string) => `/api/medical-assessment/${id}/export/grip-xml`,
  exportMlrPdf: (id: string) => `/api/medical-assessment/${id}/export/mlr-pdf`,
  /** Company-wide, not per assessment — the same rules for every report. */
  instructions: "/api/medical-assessment/instructions",
  /** The rulebook — what DECIDES what goes in a report, versioned per publish. */
  rulebook: "/api/medical-assessment/rulebook",
  rulebookPreview: "/api/medical-assessment/rulebook/preview",
  reportSections: (id: string) =>
    `/api/medical-assessment/${id}/report-sections`,
  /** What the consultation did not cover — the form's one real advantage. */
  coverage: (id: string) => `/api/medical-assessment/${id}/coverage`,
  /**
   * The consultation summary — a record of what was said, for the instructing
   * system. ⚠️ NOT part of the report; the label to show it under comes back
   * with the payload rather than being written in a client.
   */
  sessionSummary: (id: string) =>
    `/api/medical-assessment/${id}/session-summary`,
  /** §6 — which records the doctor reviewed. Saving IS the confirm. */
  recordsReview: (id: string) => `/api/medical-assessment/${id}/records-review`,
  /** The doctor answers a decision the rulebook could not work out. */
  prognosisDecision: (assessmentId: string, prognosisId: string) =>
    `/api/medical-assessment/${assessmentId}/prognoses/${prognosisId}/decision`,
  /** Signed, frozen, or signed-before-snapshots — see reportLifecycle.service.mjs. */
  reportLifecycle: (id: string) =>
    `/api/medical-assessment/${id}/report/lifecycle`,
  /** Correction: withdraw the signature so the report can be edited again. */
  reportVoidSignature: (id: string) =>
    `/api/medical-assessment/${id}/report/void-signature`,
  /** Amendment: the signed report stands; a new one supersedes it. */
  reportAmend: (id: string) => `/api/medical-assessment/${id}/report/amend`,
  examination: (id: string) => `/api/medical-assessment/${id}/examination`,
  mlrSupplements: (id: string) => `/api/medical-assessment/${id}/mlr-supplements`,
  mlrSignStatement: (id: string) =>
    `/api/medical-assessment/${id}/mlr-supplements/sign-statement`,
  mlrIngestRecordsText: (id: string) =>
    `/api/medical-assessment/${id}/mlr-supplements/ingest-records-text`,
  mlrIngestRecordsPdf: (id: string) =>
    `/api/medical-assessment/${id}/mlr-supplements/ingest-records-pdf`,
  mlrSeedRecordsFromCaseHub: (id: string) =>
    `/api/medical-assessment/${id}/mlr-supplements/seed-records-from-case-hub`,
  mlrSeedRegistrationFromDoctor: (id: string) =>
    `/api/medical-assessment/${id}/mlr-supplements/seed-registration-from-doctor`,
  caseHubIdentity: (id: string) =>
    `/api/medical-assessment/${id}/case-hub/identity`,
  doctorLink: "/api/medical-assessment/me/doctor-link",
  doctorLinkLookup: "/api/medical-assessment/me/doctor-link/lookup",
  prognoses: (id: string) => `/api/medical-assessment/${id}/prognoses`,
  prognosesSeed: (id: string) => `/api/medical-assessment/${id}/prognoses/seed`,
  prognosesTranscribeVoice: (id: string) =>
    `/api/medical-assessment/${id}/prognoses/transcribe-voice`,
  prognosisConfirm: (assessmentId: string, prognosisId: string) =>
    `/api/medical-assessment/${assessmentId}/prognoses/${prognosisId}/confirm`,
  buildersPreview: "/api/medical-assessment/builders/preview",
} as const;
